import type { ResultOf, VariablesOf } from '@graphql-typed-document-node/core';
import { print } from 'graphql';

import { graphql } from '~/shared/codegen';
import { type FragmentType, useFragment } from '~/shared/codegen/fragment-masking';
import { EnumCourseCourseType, EnumCourseLanguage, SortFindManyCourseInput } from '~/shared/codegen/graphql';
import { fetchGraphQL } from '~/shared/helpers/fetch-graphql';
import type { Locale } from '~/universal/constants';

export const LIMIT_OF_COURSES = 3;
const SORT_BY_ID_DESC = SortFindManyCourseInput.IdDesc;

export const PopularCourseItemFragment = graphql(/* GraphQL */ `
  fragment PopularCourseItemFragment on Course {
    _id
    title
    alias
    language
    courseType
    isPublished
    price
    currency
    isFreeCourse
    image {
      path
    }
    thumbnail {
      path
    }
  }
`);

export const PopularCoursesQuery = graphql(/* GraphQL */ `
  query popularCoursesQuery(
    $limit: Int!
    $freeFilter: FilterFindManyCourseInput
    $paidFilter: FilterFindManyCourseInput
    $sort: SortFindManyCourseInput
  ) {
    freeCourses: findManyCourseQuery(filter: $freeFilter, skip: 0, limit: $limit, sort: $sort) {
      ...PopularCourseItemFragment
    }

    paidCourses: findManyCourseQuery(filter: $paidFilter, skip: 0, limit: $limit, sort: $sort) {
      ...PopularCourseItemFragment
    }
  }
`);

export type PopularCoursesQueryData = ResultOf<typeof PopularCoursesQuery>;
type PopularCoursesQueryVariables = VariablesOf<typeof PopularCoursesQuery>;

export type PopularCourseItemRef = FragmentType<typeof PopularCourseItemFragment>;

export type PopularCourseMenuItem = Readonly<{
  _id: string;
  item: FragmentType<typeof PopularCourseItemFragment>;
}>;

export type PopularCoursesPair = Readonly<{
  freeCourses: PopularCourseMenuItem[];
  paidCourses: PopularCourseMenuItem[];
}>;

const coursesPairCache = new Map<string, PopularCoursesPair>();
const pendingPairRequests = new Map<string, Promise<PopularCoursesPair>>();

const mapCourses = (items: NonNullable<PopularCoursesQueryData['freeCourses']>): PopularCourseMenuItem[] => {
  return items.map((rawItem) => {
    const unwrappedItem = useFragment(PopularCourseItemFragment, rawItem);

    return {
      _id: unwrappedItem._id,
      item: rawItem,
    };
  });
};

const mapLocaleToCourseLanguage = (lng: string): EnumCourseLanguage => {
  switch (lng) {
    case 'uk':
      return EnumCourseLanguage.Ua;
    case 'ru':
      return EnumCourseLanguage.Ru;
    case 'en':
      return EnumCourseLanguage.En;
    default:
      return EnumCourseLanguage.Ua;
  }
};

export const resetPopularCoursesPairCache = (): void => {
  coursesPairCache.clear();
  pendingPairRequests.clear();
};

export const getCachedPopularCoursesPair = (lng: Locale): PopularCoursesPair | null => {
  return coursesPairCache.get(lng) ?? null;
};

export const popularCoursesPairCache = async (lng: Locale): Promise<PopularCoursesPair> => {
  const cachedValue = coursesPairCache.get(lng);

  if (cachedValue) {
    return cachedValue;
  }

  const pendingRequest = pendingPairRequests.get(lng);

  if (pendingRequest) {
    return pendingRequest;
  }

  const language = mapLocaleToCourseLanguage(lng);

  const request = fetchGraphQL<PopularCoursesQueryData, PopularCoursesQueryVariables>({
    query: print(PopularCoursesQuery),
    lng,
    variables: {
      limit: LIMIT_OF_COURSES,
      freeFilter: {
        courseType: EnumCourseCourseType.Course,
        isFreeCourse: true,
        isPublished: true,
        language,
      },
      paidFilter: {
        isFreeCourse: false,
        isPublished: true,
        language,
      },
      sort: SORT_BY_ID_DESC,
    },
  })
    .then((data) => {
      const nextValue: PopularCoursesPair = {
        freeCourses: mapCourses(data.freeCourses ?? []),
        paidCourses: mapCourses(data.paidCourses ?? []),
      };

      coursesPairCache.set(lng, nextValue);
      pendingPairRequests.delete(lng);

      return nextValue;
    })
    .catch((error: unknown) => {
      pendingPairRequests.delete(lng);
      throw error;
    });

  pendingPairRequests.set(lng, request);

  return request;
};
